import { fmtInt } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";
import { btwBlockRows, BTW_ACTUAL_ROWS, BTW_ESTIMATE_NOTE, BTW_MIXED_NOTE } from "@/lib/btw";

const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} %`);

const eur = (v) => (v === null || v === undefined ? "—" : `€ ${fmtInt(v)}`);
const signed = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}€ ${fmtInt(Math.abs(v))}`);

// Informatief BTW-blok onder een matrix: kolommen = jaren, rijen = af te dragen / voorbelasting / saldo.
// `columns`: [{ key, label, block: { vat_revenue, vat_opex, vat_balance, vat_actual, vat_diff } | null, muted }]
// `showActual`: toon ijkpunt-rijen (werkelijke afdracht uit de liquiditeitsmatrix) — alleen bij boekjaren.
// `onActualCommit(colKey, betaald)`: maakt de rij 'Werkelijke afdracht' invulbaar (+ = betaald, − = terugontvangen).
export function BtwBlock({ t, columns, regime = "plichtig", showActual = false, readOnly = false, onActualCommit, testid = "btw-block" }) {
  if (!columns?.length) return null;
  const base = btwBlockRows(regime);
  const rows = showActual ? [...base, ...BTW_ACTUAL_ROWS] : base;
  const hasActual = showActual && columns.some((c) => c.block?.vat_actual != null);
  return (
    <div className="mt-4 border-2 border-foreground bg-card heavy-shadow" data-testid={testid}>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-foreground text-background">
            <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-foreground min-w-[150px] md:min-w-[220px]">
              {t("BTW")}
              <span className="normal-case tracking-normal font-normal opacity-70 text-[10px] ml-2">{t(regime === "gemengd" ? "schatting · niet-aftrekbaar deel telt als kostenpost · bedragen excl. BTW" : "schatting · informatief · bedragen excl. BTW")}</span>
            </th>
            {columns.map((c) => (
              <th key={c.key} className={`text-right px-3 py-2 font-display uppercase text-xs tracking-widest whitespace-nowrap ${c.muted ? "opacity-70" : ""}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, label]) => {
            const saldo = key === "vat_balance" || key === "net_after_vat";
            const actualRow = key === "vat_actual" || key === "vat_diff";
            const sub = key === "vat_opex_deductible";
            const isPct = key === "pro_rata_pct";
            const trCls = saldo ? "border-t-2 border-foreground bg-muted/40" : actualRow ? "border-b border-dashed border-foreground/20 bg-secondary/5" : "border-b border-foreground/10";
            return (
              <tr key={key} className={trCls} data-testid={`btw-row-${key}`}>
                <td className={`px-3 py-1.5 sticky left-0 ${saldo ? "sticky-tint-40 font-display uppercase font-black" : actualRow ? "bg-card italic text-muted-foreground" : sub ? "bg-card pl-8 italic text-muted-foreground" : "bg-card"}`}>{t(label)}</td>
                {columns.map((c) => {
                  const v = c.block?.[key];
                  if (key === "vat_actual" && onActualCommit) {
                    return (
                      <td key={c.key} className="px-1 py-0.5" data-testid={`btw-${key}-${c.key}`}>
                        <MoneyInput value={v ?? 0} readOnly={readOnly} testid={`btw-actual-input-${c.key}`}
                          onCommit={(val) => { const n = Number(val) || 0; if (n !== (v ?? 0)) onActualCommit(c.key, n); }}
                          className={`w-full bg-transparent px-2 py-1 font-mono-num text-right border-b border-dashed border-foreground/40 focus:outline-none focus:bg-primary/10 ${readOnly ? "cursor-not-allowed opacity-70" : ""} ${v == null ? "text-muted-foreground/60" : ""}`} />
                      </td>
                    );
                  }
                  return (
                    <td key={c.key} className={`text-right px-3 py-1.5 font-mono-num whitespace-nowrap ${saldo ? "font-bold" : ""} ${c.muted || actualRow ? "text-muted-foreground" : ""}`}
                      data-testid={`btw-${key}-${c.key}`} title={actualRow && v == null ? t("Nog niet ingevuld") : undefined}>
                      {isPct ? pct(v) : saldo || actualRow ? signed(v) : eur(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <p className="px-3 py-2 text-[11px] text-muted-foreground border-t border-foreground/10">
        {t(regime === "gemengd" ? BTW_MIXED_NOTE : BTW_ESTIMATE_NOTE)}
        {showActual && ` ${hasActual
          ? t("IJkpunt: de werkelijke afdracht komt uit je BTW-aangiften over dat jaar (+ = per saldo betaald, − = terugontvangen); het verschil laat zien hoe goed de tarieven per subpost passen.")
          : t("IJkpunt: vul per jaar in wat je per saldo aan BTW hebt betaald (+) of terugontvangen (−) volgens de aangiften, om de schatting te toetsen.")}`}
      </p>
    </div>
  );
}
